import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import { AuthContext } from './user.context';
import {
  addToFavorites,
  removeFromFavorites,
} from '../utils/mobile';
import { getResellerSession } from '../utils/core/auth';

export const FavoritesContext = createContext({
  favorites: [],
  addFavorite: () => {},
  removeFavorite: () => {},
  isFavorite: () => false,
  refreshFavorites: () => {},
});

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const { currentUser } = useContext(AuthContext);

  // Initial sync when currentUser changes - optimizado con useCallback
  const syncFavoritesFromUser = useCallback(() => {
    if (currentUser?.favoriteAuctions) {
      setFavorites(currentUser.favoriteAuctions);
    } else {
      setFavorites([]);
    }
  }, [currentUser?.favoriteAuctions]);

  useEffect(() => {
    syncFavoritesFromUser();
  }, [syncFavoritesFromUser]);

  const refreshFavorites = useCallback(async () => {
    if (!currentUser?.email || currentUser?.activeApp !== 'reseller_app') {
      setFavorites([]);
      return;
    }

    try {
      const session = await getResellerSession();
      setFavorites(session?.favoriteAuctions || []);
    } catch (error) {
      console.error('Error refreshing favorites:', error);
    }
  }, [currentUser?.activeApp, currentUser?.email]);

  const addFavorite = useCallback(
    async (auctionId) => {
      if (!currentUser?.email) return { success: false };
      
      // Optimistic update - add immediately
      setFavorites((prev) => {
        if (prev.includes(auctionId)) return prev;
        return [...prev, auctionId];
      });

      try {
        const result = await addToFavorites(currentUser.email, auctionId);
        if (!result.success) {
          // Revert optimistic update on failure
          setFavorites((prev) => prev.filter((id) => id !== auctionId));
          return { success: false, error: result.error };
        }
        return { success: true };
      } catch (error) {
        // Revert optimistic update on error
        setFavorites((prev) => prev.filter((id) => id !== auctionId));
        console.error('Error adding to favorites:', error);
        return { success: false, error: error.message };
      }
    },
    [currentUser?.email]
  );

  const removeFavorite = useCallback(
    async (auctionId) => {
      if (!currentUser?.email) return { success: false };

      // Optimistic update - remove immediately
      setFavorites((prev) => prev.filter((id) => id !== auctionId));

      try {
        const result = await removeFromFavorites(currentUser.email, auctionId);
        if (!result.success) {
          // Revert optimistic update on failure
          setFavorites((prev) => [...prev, auctionId]);
          return { success: false, error: result.error };
        }
        return { success: true };
      } catch (error) {
        // Revert optimistic update on error
        setFavorites((prev) => [...prev, auctionId]);
        console.error('Error removing from favorites:', error);
        return { success: false, error: error.message };
      }
    },
    [currentUser?.email]
  );

  const isFavorite = useCallback(
    (auctionId) => {
      return favorites.includes(auctionId);
    },
    [favorites]
  );

  const value = {
    favorites,
    addFavorite,
    removeFavorite,
    isFavorite,
    refreshFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
